import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ShieldCheck, ArrowRight, EyeSlash, Eye, LockKey, IdentificationBadge,
  ArrowLeft, Pulse, Fingerprint, Scales, Certificate,
  ShieldStar, Crosshair, Lightning,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { formatApiError, api } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GovLogin() {
  const { login, user, setUserAfterLogin } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds"); // creds | otp
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);
  const [tick, setTick] = useState(0);
  // 2FA state
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMsg, setOtpMsg] = useState("");
  const [ttl, setTtl] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/branding`).then((r) => setBranding(r.data)).catch(() => {});
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // OTP TTL countdown
  useEffect(() => {
    if (step !== "otp" || ttl <= 0) return;
    const id = setInterval(() => setTtl((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [step, ttl]);

  // Already-authenticated officers shouldn't see this page — redirect to portal.
  if (user?.role === "govt") {
    return <Navigate to="/gov" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(employeeId.trim(), password);
      if (u?.requires_otp) {
        // 2FA gate — jump into OTP step
        setChallengeId(u.challenge_id);
        setOtpMsg(u.message || `Verification code sent to ${u.email}`);
        setTtl(u.ttl_seconds || 600);
        setOtp("");
        setStep("otp");
      } else if (u?.role !== "govt") {
        setErr("This portal is restricted to authorised government officers.");
      } else {
        nav("/gov", { replace: true });
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setBusy(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/auth/login/verify-otp", {
        challenge_id: challengeId, otp: otp.trim(),
      });
      localStorage.setItem("rescigo_token", r.data.access_token);
      localStorage.setItem("rescigo_user", JSON.stringify(r.data.user));
      setUserAfterLogin?.(r.data.user);
      if (r.data.user.role !== "govt") {
        setErr("This portal is restricted to authorised government officers.");
        setStep("creds");
      } else {
        nav("/gov", { replace: true });
      }
    } catch (ex) { setErr(formatApiError(ex)); }
    setBusy(false);
  };

  const resendOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login/resend-otp", {
        challenge_id: challengeId, otp: "",
      });
      setChallengeId(r.data.challenge_id);
      setTtl(r.data.ttl_seconds || 600);
      setOtpMsg(r.data.message || "New verification code sent");
      setOtp("");
    } catch (ex) { setErr(formatApiError(ex)); }
    setBusy(false);
  };

  const backToCreds = () => {
    setStep("creds"); setOtp(""); setChallengeId(""); setErr("");
  };

  const ttlMm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ttlSs = String(ttl % 60).padStart(2, "0");

  const now = new Date();
  const istHr = ((now.getUTCHours() + 5) % 24).toString().padStart(2, "0");
  const istMn = ((now.getUTCMinutes() + 30) % 60).toString().padStart(2, "0");
  const istSc = now.getUTCSeconds().toString().padStart(2, "0");

  return (
    <div className="min-h-screen flex bg-[#03090A] text-white antialiased relative overflow-hidden" data-testid="gov-login">
      {/* Background — futuristic command-room aesthetic */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(45,90,39,0.45),transparent_55%),radial-gradient(circle_at_80%_75%,rgba(230,57,70,0.18),transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        {/* Floating particles */}
        {Array.from({ length: 25 }).map((_, i) => (
          <motion.div key={i}
            className="absolute w-1 h-1 rounded-full bg-[#5C9F4F]/40"
            style={{ left: `${(i * 41) % 100}%`, top: `${(i * 29) % 100}%` }}
            animate={{ y: [0, -30, 0], opacity: [0.2, 0.7, 0.2] }}
            transition={{ duration: 4 + (i % 5), repeat: Infinity, delay: i * 0.18 }}
          />
        ))}
      </div>

      {/* LEFT — enforcement aesthetic visual */}
      <div className="hidden lg:flex lg:w-[55%] relative border-r border-white/[0.06] overflow-hidden">
        <ScopeReticle />
        <CrestSeal />
        <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
          <div className="flex items-center gap-4">
            <img
              src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"}
              alt="Rescigo Health"
              className="h-9 w-auto opacity-95"
              data-testid="gov-login-logo"
            />
            <div className="hidden xl:block h-9 w-px bg-white/10" />
            <div className="hidden xl:block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C9F4F] font-medium">Sovereign</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Enforcement Console</div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                      className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[10px] uppercase tracking-[0.28em] text-[#FF8A95] font-semibold mb-6">
              <Crosshair size={11} weight="bold" /> Classified · Officer Tier
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl tracking-tighter leading-[0.95] font-medium">
              The <span className="text-[#E63946]">authority</span><br />
              layer for<br />
              waste <span className="italic font-light text-[#E8F0E7]">jurisdiction</span>.
            </h1>
            <p className="text-slate-300 mt-6 text-sm leading-relaxed max-w-md">
              Read-only operational visibility for sovereign departments. Every record is
              cryptographically signed, geo-stamped, and discoverable in court.
            </p>

            <div className="grid grid-cols-3 gap-3 mt-10">
              <Stat I={Scales}     label="Court-admissible" value="ISO 27001" />
              <Stat I={Certificate} label="Standard" value="CPCB BMW '16" />
              <Stat I={Fingerprint} label="Trail" value="Tamper-proof" />
            </div>
          </motion.div>

          <div className="flex items-end justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-600 mb-1">System time · IST</div>
              <div className="font-mono text-3xl tabular-nums tracking-wider text-[#E8F0E7]" data-testid="gov-clock">
                {istHr}:{istMn}<span className="text-[#5C9F4F] animate-pulse">:</span>{istSc}
              </div>
              <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#5C9F4F] animate-pulse" />
                Network secure · TLS 1.3 · Session zone IN
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-600 mb-1">Active officers</div>
              <div className="font-mono text-xl tabular-nums text-[#E8F0E7]">{47 + (tick % 4)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — secure form */}
      <div className="flex-1 flex flex-col">
        <div className="px-6 lg:px-12 pt-6 pb-2 flex items-center justify-between">
          <Link to="/government" data-testid="gov-back" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition">
            <ArrowLeft size={11} weight="bold" /> Public portal
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] text-slate-500">
            <Pulse size={10} weight="fill" color="#E63946" /> Officer Authentication
          </span>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7 }} className="w-full max-w-md">
            <div className="lg:hidden mb-6 flex items-center gap-3">
              <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-8 w-auto opacity-95" />
              <span className="text-[9px] uppercase tracking-[0.28em] text-[#5C9F4F]">Sovereign Console</span>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[10px] uppercase tracking-[0.28em] text-[#FF8A95] font-semibold" data-testid="gov-restricted">
              <ShieldStar size={11} weight="fill" /> Authorised Officers Only
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-medium mt-5 leading-tight">
              Officer<br />
              <span className="italic font-light text-[#E8F0E7]">authentication</span> required
            </h2>
            <p className="text-sm text-slate-400 mt-3">
              Sign in with your authorised credentials. All sessions are
              cryptographically signed and entered into the audit ledger.
            </p>

            {step === "creds" && (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="gov-login-form">
              <Field
                label="Officer ID / Email"
                Icon={IdentificationBadge}
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="GOV-1024 or officer@dept.gov.in"
                testid="gov-officer-id"
                autoFocus
              />
              <Field
                label="Authentication code"
                Icon={LockKey}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                testid="gov-password"
                type={showPwd ? "text" : "password"}
                trailing={
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                          className="p-1.5 text-slate-500 hover:text-slate-200" tabIndex={-1}>
                    {showPwd ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </button>
                }
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-[11px] text-slate-400">
                  <input type="checkbox" className="accent-[#5C9F4F] w-3.5 h-3.5" /> Trusted endpoint · 4h session
                </label>
                <a href="mailto:gov-support@rescigo.health" className="text-[11px] text-slate-400 hover:text-white">Request access</a>
              </div>
              {err && (
                <div className="text-xs text-[#FF8A95] bg-[#E63946]/10 border border-[#E63946]/20 rounded-lg px-3 py-2 inline-flex items-start gap-2" data-testid="gov-error">
                  <ShieldStar size={12} className="mt-0.5 shrink-0" /> {err}
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="gov-login-submit"
                      className="group relative w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-xl font-medium inline-flex items-center justify-center gap-2 shadow-lg shadow-green-900/40 transition-all overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <Lightning size={14} weight="fill" className="relative" />
                <span className="relative">{busy ? "Authenticating…" : "Authenticate & enter portal"}</span>
                <ArrowRight size={14} weight="bold" className="relative" />
              </button>
            </form>
            )}

            {step === "otp" && (
            <form onSubmit={submitOtp} className="mt-8 space-y-4" data-testid="gov-otp-form">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C9F4F] flex items-center gap-2">
                <Fingerprint size={11} weight="fill"/> Two-factor verification
              </div>
              <div className="text-sm text-slate-200">{otpMsg}</div>
              <Field
                label="8-digit verification code"
                Icon={LockKey}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="• • • • • • • •"
                testid="gov-otp-input"
                autoFocus
                mono
              />
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>Expires in <span className="font-mono text-[#5C9F4F]">{ttlMm}:{ttlSs}</span></span>
                <button type="button" onClick={resendOtp} disabled={busy || ttl > 540}
                        data-testid="gov-otp-resend"
                        className="hover:text-white disabled:opacity-40">
                  Resend code
                </button>
              </div>
              {err && (
                <div className="text-xs text-[#FF8A95] bg-[#E63946]/10 border border-[#E63946]/20 rounded-lg px-3 py-2 inline-flex items-start gap-2" data-testid="gov-error">
                  <ShieldStar size={12} className="mt-0.5 shrink-0" /> {err}
                </div>
              )}
              <button type="submit" disabled={busy || otp.length !== 8} data-testid="gov-otp-submit"
                      className="group relative w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-xl font-medium inline-flex items-center justify-center gap-2 shadow-lg shadow-green-900/40 transition-all overflow-hidden">
                <Lightning size={14} weight="fill" className="relative" />
                <span className="relative">{busy ? "Verifying…" : "Verify & enter portal"}</span>
                <ArrowRight size={14} weight="bold" className="relative" />
              </button>
              <button type="button" onClick={backToCreds}
                      className="w-full h-10 text-xs text-slate-400 hover:text-white inline-flex items-center justify-center gap-1">
                <ArrowLeft size={12}/> Use different credentials
              </button>
            </form>
            )}

            {/* shared security strip */}
            <div className="mt-6 grid grid-cols-3 gap-2 pt-4 border-t border-white/5">
              <Sec label="MFA" status="armed" />
              <Sec label="Audit-ledger" status="recording" />
              <Sec label="Geo-fence" status="IN-only" />
            </div>
            <div className="flex items-start gap-2 text-[10px] text-slate-500 leading-relaxed pt-2">
              <ShieldCheck size={11} weight="fill" className="text-[#5C9F4F] mt-0.5 shrink-0" />
              <span>
                Unauthorised access is monitored and prosecuted under the
                Information Technology Act, 2000 (Sec 66) and the Indian Penal Code.
                By proceeding you acknowledge that this terminal will record
                your IP address, device fingerprint, and session metadata for
                audit and discovery.
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, Icon, trailing, testid, mono, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold block mb-1.5">{label}</label>
    <div className="relative group">
      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#5C9F4F] transition" />
      <input
        {...props}
        data-testid={testid}
        className={`w-full h-12 pl-10 pr-10 bg-white/[0.04] border border-white/10 rounded-xl text-sm placeholder-slate-600 tracking-wide focus:outline-none focus:border-[#5C9F4F]/60 focus:ring-2 focus:ring-[#5C9F4F]/15 focus:bg-white/[0.06] transition ${mono ? "font-mono text-center text-xl tracking-[0.5em]" : "font-mono"}`}
      />
      {trailing && <div className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  </div>
);

const Sec = ({ label, status }) => (
  <div className="text-center">
    <div className="text-[8px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
    <div className="text-[10px] text-[#5C9F4F] font-mono mt-0.5 inline-flex items-center gap-1">
      <span className="inline-block w-1 h-1 rounded-full bg-[#5C9F4F] animate-pulse" /> {status}
    </div>
  </div>
);

const Stat = ({ I, label, value }) => (
  <div className="rounded-lg p-3 bg-white/[0.04] border border-white/10">
    <I size={14} color="#5C9F4F" weight="duotone" />
    <div className="text-[8px] uppercase tracking-[0.24em] text-slate-500 mt-2">{label}</div>
    <div className="text-[11px] text-[#E8F0E7] mt-0.5 font-medium">{value}</div>
  </div>
);

// Decorative SVG — animated targeting reticle scope
const ScopeReticle = () => (
  <svg className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-12 opacity-30 pointer-events-none" width="700" height="700" viewBox="0 0 700 700" aria-hidden>
    {Array.from({ length: 5 }).map((_, i) => (
      <circle key={i} cx="350" cy="350" r={80 + i * 70} fill="none" stroke="#5C9F4F" strokeWidth="0.6" strokeOpacity={0.35 - i * 0.05} strokeDasharray="2 6">
        <animateTransform attributeName="transform" type="rotate" from={i % 2 ? "0 350 350" : "360 350 350"} to={i % 2 ? "360 350 350" : "0 350 350"}
                          dur={`${30 + i * 15}s`} repeatCount="indefinite" />
      </circle>
    ))}
    <line x1="50" y1="350" x2="650" y2="350" stroke="#5C9F4F" strokeOpacity="0.2" strokeWidth="0.5" />
    <line x1="350" y1="50" x2="350" y2="650" stroke="#5C9F4F" strokeOpacity="0.2" strokeWidth="0.5" />
    <circle cx="350" cy="350" r="3" fill="#E63946">
      <animate attributeName="r" values="2;5;2" dur="2s" repeatCount="indefinite" />
    </circle>
    <circle cx="350" cy="350" r="50" fill="none" stroke="#E63946" strokeOpacity="0.4" strokeDasharray="6 4">
      <animate attributeName="r" values="20;80;20" dur="3.5s" repeatCount="indefinite" />
      <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="3.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

// Decorative national-crest-style seal in the corner
const CrestSeal = () => (
  <svg className="absolute bottom-12 right-12 opacity-15 pointer-events-none" width="200" height="200" viewBox="0 0 200 200" aria-hidden>
    <circle cx="100" cy="100" r="95" fill="none" stroke="#E8F0E7" strokeWidth="0.8" />
    <circle cx="100" cy="100" r="80" fill="none" stroke="#E8F0E7" strokeWidth="0.4" />
    {Array.from({ length: 24 }).map((_, i) => {
      const a = (i * 15 * Math.PI) / 180;
      const x1 = 100 + 80 * Math.cos(a);
      const y1 = 100 + 80 * Math.sin(a);
      const x2 = 100 + 95 * Math.cos(a);
      const y2 = 100 + 95 * Math.sin(a);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E8F0E7" strokeWidth="0.6" />;
    })}
    <text x="100" y="105" textAnchor="middle" fill="#E8F0E7" fontSize="10" fontFamily="ui-monospace, monospace" letterSpacing="2">
      RESCIGO · GOV
    </text>
  </svg>
);
